import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { IncomeExpenseDropdownComponent } from '@app/shared/component/income-expense-dropdown/income-expense-dropdown.component';
import { DocumentService } from '@app/shared/services/vault/document.service';
import { ShareService } from '@app/shared/services/vault/share.service';
import { ToastrService } from 'ngx-toastr';
import { Subscription, timer } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import * as moment from 'moment';
import { UserService } from '@app/shared/services';
import { AttributesService } from './attributes.service';
import { EventNames, EVENTS } from '@app/constants/events.constants';
import { take } from 'rxjs/operators';
import { SharedConfirmationDialogComponent } from '@app/shared/component/shared-confirmation-dialog/shared-confirmation-dialog.component';
import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';

export const MY_FORMATS = {
  parse: {
    dateInput: 'LL',
  },
  display: {
    dateInput: 'LL',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@AutoUnsubscribe()
@Component({
  selector: 'attributes-dialog',
  templateUrl: './attributes-dialog.component.html',
  styleUrls: ['./attributes-dialog.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AttributesDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('incomeExpenseDropdown') incomeExpenseDropdown: IncomeExpenseDropdownComponent;
  @ViewChild('formTemplate') formTemplate: ElementRef;

  @Input() inputData;
  @Input() hide_mark_complete = false;

  @Output() formSetupSideBarEmitter = new EventEmitter<any>();

  today = new Date();
  premiumForm: UntypedFormGroup;
  subscription: Subscription = new Subscription();
  cryptoItems = ['one', 'two'];
  focusId = '';
  hasMaxDate = false;
  maxDate = new Date();
  moment = moment;
  @Input() fromSetup = false;
  hasCompletedSavingFrom = false;
  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    @Optional() private dialogRef: MatDialogRef<AttributesDialogComponent>,
    private fb: UntypedFormBuilder,
    private documentService: DocumentService,
    private toastr: ToastrService,
    private cd: ChangeDetectorRef,
    private shareService: ShareService,
    private http: HttpClient,
    private userService: UserService,
    private attributesService: AttributesService,
    private dialog: MatDialog
  ) {}
  currencies = [];
  searchResults = [];
  insuranceDropDownList = [];
  filteredSearchResults = [];
  hasMetalApi = false;
  hasYahooApi = false;
  has_api_call = undefined;
  powered_by = '';
  @Output() btnDisabledEmitter = new EventEmitter(true);
  async ngOnInit(): Promise<any> {
    this.subscription.add(
      this.http.get<any>('/assets/json/currencies-list.json').subscribe((data) => {
        this.currencies = Object.keys(data);
        this.cd.detectChanges();
      })
    );

    if (
      this.data?.triggeredFrom == 'register-balance' ||
      this.data?.existingAttributesList?.value?.vault_form_code == 'has_purchase_price' ||
      this.inputData?.vault_form_code == 'has_purchase_price' ||
      this.data?.existingAttributesList?.value?.History ||
      this.inputData?.History
    ) {
      this.hasMaxDate = true;
    }
    if (!!this.data?.existingAttributesList?.value?.has_api_call || !!this.inputData?.existingAttributesList?.value?.has_api_call) {
      const { has_api_call, powered_by } = !!this.inputData
        ? this.inputData?.existingAttributesList?.value
        : this.data?.existingAttributesList?.value;
      this.has_api_call = has_api_call;
      this.powered_by = powered_by;
      if (has_api_call == 'crypto-currency') {
        this.subscription.add(
          this.userService.getCryptoCurrencySymbols().subscribe((apiRes) => {
            const apiData = Object.keys(apiRes).map((key) => {
              return { key: key, value: apiRes[key] };
            });
            this.searchResults = apiData;
            this.filteredSearchResults = apiData;
            this.cd.detectChanges();
          })
        );
      }
      if (has_api_call == 'foreign-currency') {
        this.subscription.add(
          this.userService.getForeignCurrencySymbols().subscribe((apiRes) => {
            const apiData = Object.keys(apiRes).map((key) => {
              return { key: key, value: apiRes[key] };
            });

            this.searchResults = this.moveUSDtoFirst(apiData);
            this.filteredSearchResults = this.moveUSDtoFirst(apiData);
            this.cd.detectChanges();
          })
        );
      }
      if (has_api_call == 'metal-symbols') {
        this.subscription.add(
          this.userService.getMetalSymbols().subscribe((apiRes) => {
            const apiData = Object.keys(apiRes).map((key) => {
              return { key: key, value: apiRes[key] };
            });
            this.searchResults = apiData;
            this.filteredSearchResults = apiData;
            this.cd.detectChanges();
          })
        );
      }
      if (has_api_call == 'ticker-symbols') {
        this.subscription.add(
          this.userService.getTickerSymbols().subscribe((apiRes) => {
            const apiData = apiRes.map((key) => {
              return { key: key.symbol, value: key.name };
            });
            this.searchResults = apiData;
            this.filteredSearchResults = apiData;
            this.cd.detectChanges();
          })
        );
      }
    }
    this.subscription.add(
      this.documentService.getInsuranceDropDownList().subscribe(
        (res) => {
          this.insuranceDropDownList = res;
        },
        (error) => {}
      )
    );
    this.handleExistingAttributesListForTemplate();
  }
  moveUSDtoFirst(arr) {
    // Find the index of the object with key 'USD'
    const index = arr.findIndex((item) => item.key === 'USD');

    // If the object is found, move it to the first position
    if (index !== -1) {
      const [usdObject] = arr.splice(index, 1);
      arr.unshift(usdObject);
    }

    return arr;
  }
  handleSearch(e: any, canSearchForKeyValue?: boolean) {
    const event = e.trim();
    if (canSearchForKeyValue) {
      this.filteredSearchResults = this.searchResults.filter(
        (item) => item.value.trim().toLowerCase().includes(event.toLowerCase()) || item.key.trim().toLowerCase().includes(event.toLowerCase())
      );
    } else {
      this.filteredSearchResults = this.searchResults.filter((item) => item.value.trim().toLowerCase().includes(event.toLowerCase()));
    }
  }

  closeDialog(val?: string) {
    if (this.data.isFromItemsToResolveTemplate) {
      const sharedDialogRef = this.dialog.open(SharedConfirmationDialogComponent, {
        width: '400px',
        data: {
          title: 'Exit without saving?',
          materialIcon: 'info_outline',
          icon: 'close',
          networkRemoveText: `<span class="text-[#18191C] font-normal text-sm">  You are about to close  <span class="">${this.data.triggeredFrom}</span>? Do you want to save your progress? </span>`,
          proceedTitle: 'Save',
          darkText: true,
          blueBg: true,
          backBUttonTitle: "Don't Save",
        },
      });
      this.subscription.add(
        sharedDialogRef.afterClosed().subscribe((data) => {
          if (!!data) {
            this.submitForm();
          } else {
            const dialogCloseData = {
              isSuccess: true,
            };
            this.dialogRef.close(dialogCloseData);
          }
        })
      );
    } else {
      this.dialogRef.close(val);
    }
  }

  ngOnDestroy() {
    this.attributesService.isSetupDoneSubmittingAttributes.next(false);
    this.subscription.unsubscribe();
    this.userService.cleanupWorker();
  }

  formKeys = [];
  handleExistingAttributesListForTemplate() {
    const formControls: { [key: string]: any } = {};
    // ... Dynamic Logic here
    if (!!this.inputData) {
      this.inputData?.attributes?.forEach((input) => {
        const { key, type, title, required } = input;
        const isNotEditable = input?.default || input?.multiply || input?.subtract;
        let value: Date | string | number | undefined = '';

        if (key == 'effective_date' || key == 'as_of' || key.includes('as_of')) {
          value = new Date();
        } else {
          value =
            !!this.inputData?.existingAttributesList?.getFormValues && !!`${this.data?.existingAttributesList?.getFormValues[key]}`
              ? typeof type == 'string' && type?.toLowerCase() !== 'switch'
                ? this.inputData?.existingAttributesList?.getFormValues[key]
                : !!this.inputData?.existingAttributesList?.getFormValues[key]
              : !!this.inputData?.balanceData?.unstringData && this.inputData?.balanceData?.unstringData[key]
              ? this.inputData?.balanceData?.unstringData[key]
              : typeof type == 'string' && type?.toLowerCase() == 'switch'
              ? false
              : '';
          if (type == 'Dollar Input' || type == 'Number Input') {
            value = value?.toString().replace(/,/g, '');
          }
        }

        let val = value ?? '';
        if (key.includes('as_of')) {
          value = new Date();
          val = new Date();
        }

        if (
          (!!isNotEditable && isNotEditable?.toLowerCase() == 'not editable') ||
          typeof isNotEditable == 'object' ||
          typeof isNotEditable == 'string'
        ) {
          if (
            key.includes('as_of') ||
            key.includes('effective_date') ||
            (typeof type == 'string' && type.toLowerCase().includes('date') && !input?.addition)
          ) {
            formControls[key?.toLowerCase()] = [new Date(), [Validators.required]];
            val = new Date();

            this.formKeys.push({
              key,
              title,
              type,
              isNotEditable,
              value: val ?? '',
            });
          } else if (!!input?.multiply) {
            let result = [];
            input?.multiply?.forEach((item) => {
              if (formControls.hasOwnProperty(item)) {
                let parsedVal =
                  typeof formControls[item][0] == 'string' && formControls[item][0].trim() !== ''
                    ? parseFloat(formControls[item][0]?.replace(/,/g, '')) ?? 0
                    : formControls[item][0];
                val = 0;
                if (typeof parsedVal == 'number') {
                } else if (typeof parsedVal == 'string' && parsedVal.trim() == '') {
                  parsedVal = 0;
                }
                if (Number.isNaN(parsedVal)) {
                  parsedVal = 0;
                }
                if (item.toLowerCase().includes('percentage')) {
                  parsedVal = parsedVal / 100;
                }
                result.push(parsedVal);
              }
            });
            val = result.length > 0 ? result?.reduce((accumulator, currentValue) => accumulator * currentValue, 1).toFixed(8) : 0;
            formControls[key?.toLowerCase()] = [val];
            this.formKeys.push({
              key,
              title,
              type,
              isNotEditable,
              value: val,
            });
          } else if (!!input?.subtract) {
            let result = [];
            input?.subtract?.forEach((item) => {
              if (formControls.hasOwnProperty(item)) {
                const parsedVal =
                  typeof formControls[item][0] == 'string' && formControls[item][0].trim() !== ''
                    ? parseFloat(formControls[item][0]?.replace(/,/g, '')) ?? 0
                    : formControls[item][0];
                val = 0;
                result.push(parsedVal);
              }
            });
            val = result.length > 0 ? result?.reduce((accumulator, currentValue) => accumulator - currentValue).toFixed(8) : 0;
            formControls[key?.toLowerCase()] = [val];
            this.formKeys.push({
              key,
              title,
              type,
              isNotEditable,
              value: val,
            });
          } else if (!!input?.addition) {
            let result = [];
            input?.addition?.forEach((item, index) => {
              if (formControls.hasOwnProperty(item)) {
                if (type.toLowerCase().includes('date')) {
                  const date = formControls[item][0] !== '' ? new Date(formControls[item][0]) : new Date();
                  index == 0 ? result.push(date) : result.push(formControls[item][0]);
                } else {
                  const parsedVal =
                    typeof formControls[item][0] == 'string' && formControls[item][0].trim() !== ''
                      ? parseFloat(formControls[item][0]?.replace(/,/g, '')) ?? 0
                      : formControls[item][0];
                  val = 0;
                  if (typeof parsedVal == 'number') result.push(parsedVal);
                  if (typeof parsedVal == 'string' && parsedVal.trim() == '') result.push(0);
                }
              }
            });
            if (result[0] instanceof Date) {
              val = this.getDateAfterAddingMonths(result[0], result[1]);
              formControls[key?.toLowerCase()] = [this.getDateAfterAddingMonths(result[0], result[1])];
              this.formKeys.push({
                key,
                title,
                type,
                isNotEditable,
                value: val,
              });
            } else {
              val =
                result.length > 0
                  ? result
                      ?.reduce((accumulator, currentValue) => accumulator + currentValue)
                      .toFixed(typeof input.type == 'string' && input.type !== 'Text Input' ? 8 : 0)
                  : 0;
              formControls[key?.toLowerCase()] = [val];
              this.formKeys.push({
                key,
                title,
                type,
                isNotEditable,
                value: val,
              });
            }
          } else {
            if (key.includes('market_value_for_type')) value = val = 0;
            formControls[key?.toLowerCase()] = [value ?? ''];
            if (Array.isArray(type)) {
              if (!!value) {
                formControls[key?.toLowerCase()] = [value ?? ''];
              } else {
                formControls[key?.toLowerCase()] = [!!input?.defaultType ? input?.defaultType : ''];
              }
              this.formKeys.push({
                key,
                title,
                type,
                value: value ?? '',
              });
            } else {
              if (isNotEditable == 'Not Editable' && typeof type == 'string') {
                if (key == 'reporting') {
                  value =
                    type?.toLowerCase() == 'assets & liabilities'
                      ? this.inputData?.attributeClasses?.attribute_sub_class ?? ''
                      : type?.toLowerCase() == 'income & expense'
                      ? this.inputData?.attributeClasses?.income_expense_sub_class ?? ''
                      : '';
                  val =
                    type?.toLowerCase() == 'assets & liabilities'
                      ? this.inputData?.attributeClasses?.attribute_sub_class ?? ''
                      : type?.toLowerCase() == 'income & expense'
                      ? this.inputData?.attributeClasses?.income_expense_sub_class ?? ''
                      : '';
                  formControls[key?.toLowerCase()] = [value ?? ''];

                  this.formKeys.push({
                    key,
                    title,
                    type,
                    isNotEditable,
                    reportingType: input.reportingType,
                    value: val ?? '',
                  });
                } else {
                  value = type;
                  val = type;
                  if (key.includes('market_value_for_type')) value = val = 0;
                  formControls[key?.toLowerCase()] = [value ?? ''];

                  this.formKeys.push({
                    key,
                    title,
                    type,
                    isNotEditable,
                    value: val ?? '',
                  });
                }
              } else if (
                (typeof type == 'string' && isNotEditable?.toLowerCase() == 'editable') ||
                (typeof type == 'string' && isNotEditable?.toLowerCase().includes('editable'))
              ) {
                this.formKeys.push({
                  key,
                  title,
                  type,
                  value: val ?? '',
                });
              } else {
                this.formKeys.push({
                  key,
                  title,
                  type,
                  isNotEditable,
                  value: val ?? '',
                });
              }
            }
          }
        } else {
          if (key == 'reporting') {
            if (value) {
              formControls[key?.toLowerCase()] = [value ?? ''];
              this.formKeys.push({
                key,
                title,
                type,
                value: value,
                reportingType: input.reportingType,
                attributeClass: value,
              });
            } else {
              value =
                type?.toLowerCase() == 'assets & liabilities'
                  ? this.inputData?.attributeClasses?.attribute_sub_class ?? ''
                  : type?.toLowerCase() == 'income & expense'
                  ? this.inputData?.attributeClasses?.income_expense_sub_class ?? ''
                  : '';
              val =
                type?.toLowerCase() == 'assets & liabilities'
                  ? this.inputData?.attributeClasses?.attribute_sub_class ?? ''
                  : type?.toLowerCase() == 'income & expense'
                  ? this.inputData?.attributeClasses?.income_expense_sub_class ?? ''
                  : '';
              formControls[key?.toLowerCase()] = [value ?? ''];
              this.formKeys.push({
                key,
                title,
                type,
                value: val ?? '',
                reportingType: input.reportingType,
                attributeClass: value,
              });
            }
          } else {
            if (!!this.data?.existingAttributesList?.value?.History) {
              formControls[key?.toLowerCase()] = [val ?? ''];
            } else {
              formControls[key?.toLowerCase()] =
                key.includes('as_of') || key.toLowerCase().includes('date') ? [value ? value : new Date()] : [val ?? ''];
            }
            if (Array.isArray(this.handleDropDownTypes(input))) {
              if (!val) {
                formControls[key?.toLowerCase()] = [!!input?.defaultType ? input?.defaultType : '', !!required ? [Validators.required] : []];
              } else {
                formControls[key?.toLowerCase()] = !!required
                  ? [value ?? val ? (this.toNumber(value ?? val) ? `${Number(value ?? val)}` : value ?? val) : '', [Validators.required]]
                  : [value ?? val ? (this.toNumber(value ?? val) ? `${Number(value ?? val)}` : value ?? val) : ''];
              }
              if (key.includes('commitment_term_in_months')) {
                formControls[key?.toLowerCase()] = [`${Number(value ?? val)}`];
              }
            }
            this.formKeys.push({
              key,
              title,
              type,
              value: val ? `${val}` : '',
              required: !!required,
            });
          }
        }
      });
    } else {
      this.data?.hide_mark_complete ? (this.hide_mark_complete = true) : (this.hide_mark_complete = false);
      this.data?.existingAttributesList?.value?.inputs?.forEach((input) => {
        const { key, type, title, defaultType, required } = input;
        const isNotEditable = input?.default || input?.multiply || input?.subtract;
        let value: Date | string | number | undefined = '';

        if (key == 'effective_date' || key == 'as_of' || key.includes('as_of')) {
          value = this.data?.triggeredFrom == 'register-balance' ? this.data?.balanceData?.effective_date ?? new Date() : new Date();
        } else {
          value =
            !!this.data?.existingAttributesList?.getFormValues && !!`${this.data?.existingAttributesList?.getFormValues[key]}`
              ? typeof type == 'string' && type?.toLowerCase() != 'switch'
                ? this.data?.existingAttributesList?.getFormValues[key]
                : !!this.data?.existingAttributesList?.getFormValues[key]
              : !!this.data?.balanceData?.unstringData && this.data?.balanceData?.unstringData[key]
              ? this.data?.balanceData?.unstringData[key]
              : typeof type == 'string' && type?.toLowerCase() == 'switch'
              ? false
              : '';
          if (type == 'Dollar Input' || type == 'Number Input') {
            value = value?.toString().replace(/,/g, '');
          }
        }
        !!defaultType && value == '' ? (value = defaultType) : '';
        let val = value ?? '';

        if (key.includes('as_of')) {
          value = new Date();
          val = new Date();
        }
        if (
          (!!isNotEditable && isNotEditable?.toLowerCase() == 'not editable') ||
          typeof isNotEditable == 'object' ||
          typeof isNotEditable == 'string'
        ) {
          if (
            key?.includes('as_of') ||
            key?.includes('effective_date') ||
            (typeof type == 'string' && type?.toLowerCase().includes('date') && !input?.addition)
          ) {
            if (this.data?.triggeredFrom == 'register-balance') {
              formControls[key?.toLowerCase()] = [this.data?.balanceData?.effective_date ?? new Date(), [Validators.required]];
            } else {
              formControls[key?.toLowerCase()] = [new Date(), [Validators.required]];
            }
            val = new Date();
            this.formKeys.push({
              key,
              title,
              type,
              isNotEditable,
              value: val ?? '',
            });
          } else if (key.includes('value_per_share')) {
            value = val = 0;
            // if (key.include('value_per_share')) value = val = 0;
            formControls[key?.toLowerCase()] = [value ?? ''];
            this.formKeys.push({
              key,
              title,
              type,
              isNotEditable,
              value: val ?? '',
            });
          } else if (!!input?.multiply) {
            let result = [];
            input?.multiply?.forEach((item) => {
              if (formControls.hasOwnProperty(item)) {
                let parsedVal =
                  typeof formControls[item][0] == 'string' && formControls[item][0].trim() !== ''
                    ? parseFloat(formControls[item][0]?.replace(/,/g, '')) ?? 0
                    : formControls[item][0];
                val = 0;
                if (typeof parsedVal == 'number') {
                } else if (typeof parsedVal == 'string' && parsedVal.trim() == '') {
                  parsedVal = 0;
                }
                if (Number.isNaN(parsedVal)) {
                  parsedVal = 0;
                }

                if (item.toLowerCase().includes('percentage')) {
                  parsedVal = parsedVal / 100;
                }
                result.push(parsedVal);
              }
            });

            val = result.length > 0 ? result?.reduce((accumulator, currentValue) => accumulator * currentValue, 1).toFixed(8) : 0;
            formControls[key?.toLowerCase()] = [val];
            this.formKeys.push({
              key,
              title,
              type,
              isNotEditable,
              value: val,
            });
          } else if (!!input?.subtract) {
            let result = [];
            input?.subtract?.forEach((item) => {
              if (formControls.hasOwnProperty(item)) {
                const parsedVal =
                  typeof formControls[item][0] == 'string' && formControls[item][0].trim() !== ''
                    ? parseFloat(formControls[item][0]?.replace(/,/g, '')) ?? 0
                    : formControls[item][0];
                val = 0;
                result.push(parsedVal);
              }
            });
            val = result.length > 0 ? result?.reduce((accumulator, currentValue) => accumulator - currentValue).toFixed(8) : 0;
            formControls[key?.toLowerCase()] = [val];
            this.formKeys.push({
              key,
              title,
              type,
              isNotEditable,
              value: val,
            });
          } else if (!!input?.addition) {
            let result = [];

            input?.addition?.forEach((item, index) => {
              if (formControls.hasOwnProperty(item)) {
                if (type.toLowerCase().includes('date')) {
                  const date = new Date(formControls[item][0]);
                  index == 0 ? result.push(date) : result.push(formControls[item][0]);
                } else {
                  const parsedVal =
                    typeof formControls[item][0] == 'string' && formControls[item][0].trim() !== ''
                      ? parseFloat(formControls[item][0]?.replace(/,/g, '')) ?? 0
                      : formControls[item][0];
                  val = 0;
                  if (typeof parsedVal == 'number') result.push(parsedVal);
                  if (typeof parsedVal == 'string' && parsedVal.trim() == '') result.push(0);
                }
              }
            });

            if (result[0] instanceof Date) {
              val = this.getDateAfterAddingMonths(result[0], result[1]);
              formControls[key?.toLowerCase()] = [this.getDateAfterAddingMonths(result[0], result[1])];
              this.formKeys.push({
                key,
                title,
                type,
                isNotEditable,
                value: val,
              });
            } else {
              val =
                result.length > 0
                  ? result
                      ?.reduce((accumulator, currentValue) => accumulator + currentValue)
                      .toFixed(typeof input.type == 'string' && input.type !== 'Text Input' ? 8 : 0)
                  : 0;
              formControls[key?.toLowerCase()] = [val];
              this.formKeys.push({
                key,
                title,
                type,
                isNotEditable,
                value: val,
              });
            }
          } else {
            if (key.includes('market_value_for_type')) {
              if (!!this.data?.balanceData?.unstringData && this.data?.balanceData?.unstringData[key]) {
                value = val = this.data?.balanceData?.unstringData[key];
              } else {
                value = val = 0;
              }
            }
            formControls[key?.toLowerCase()] = [value ?? ''];
            if (Array.isArray(type)) {
              if (!!value) {
                formControls[key?.toLowerCase()] = [value ?? ''];
              } else {
                formControls[key?.toLowerCase()] = [!!input?.defaultType ? input?.defaultType : ''];
              }
              this.formKeys.push({
                key,
                title,
                type,
                value: value ? value : !!input?.defaultType ? input?.defaultType : '',
              });
            } else {
              if (isNotEditable == 'Not Editable' && typeof type == 'string') {
                if (key == 'reporting') {
                  value =
                    type?.toLowerCase() == 'assets & liabilities'
                      ? this.data?.attributeClasses?.attribute_sub_class ?? ''
                      : type?.toLowerCase() == 'income & expense'
                      ? this.data?.attributeClasses?.income_expense_sub_class ?? ''
                      : '';
                  val =
                    type?.toLowerCase() == 'assets & liabilities'
                      ? this.data?.attributeClasses?.attribute_sub_class ?? ''
                      : type?.toLowerCase() == 'income & expense'
                      ? this.data?.attributeClasses?.income_expense_sub_class ?? ''
                      : '';
                  formControls[key?.toLowerCase()] = [value ?? ''];

                  this.formKeys.push({
                    key,
                    title,
                    type,
                    isNotEditable,
                    reportingType: input.reportingType,
                    value: val ?? '',
                  });
                } else {
                  value = type;
                  val = type;
                  if (key.includes('market_value_for_type')) {
                    if (!!this.data?.balanceData?.unstringData && this.data?.balanceData?.unstringData[key]) {
                      value = val = this.data?.balanceData?.unstringData[key];
                    } else {
                      value = val = 0;
                    }
                  }

                  // if (key.include('value_per_share')) value = val = 0;
                  formControls[key?.toLowerCase()] = [value ?? ''];
                  this.formKeys.push({
                    key,
                    title,
                    type,
                    isNotEditable,
                    value: val ?? '',
                  });
                }
              } else if (
                (typeof type == 'string' && isNotEditable?.toLowerCase() == 'editable') ||
                (typeof type == 'string' && isNotEditable?.toLowerCase().includes('editable'))
              ) {
                this.formKeys.push({
                  key,
                  title,
                  type,
                  value: val ?? '',
                });
              } else {
                this.formKeys.push({
                  key,
                  title,
                  type,
                  isNotEditable,
                  value: val ?? '',
                });
              }
            }
          }
        } else {
          if (key == 'reporting') {
            if (value) {
              formControls[key?.toLowerCase()] = [value ?? ''];
              this.formKeys.push({
                key,
                title,
                type,
                value: value,
                reportingType: input.reportingType,
                attributeClass: value,
              });
            } else {
              value =
                type?.toLowerCase() == 'assets & liabilities'
                  ? this.data?.attributeClasses?.attribute_sub_class ?? ''
                  : type?.toLowerCase() == 'income & expense'
                  ? this.data?.attributeClasses?.income_expense_sub_class ?? ''
                  : '';
              val =
                type?.toLowerCase() == 'assets & liabilities'
                  ? this.data?.attributeClasses?.attribute_sub_class ?? ''
                  : type?.toLowerCase() == 'income & expense'
                  ? this.data?.attributeClasses?.income_expense_sub_class ?? ''
                  : '';
              formControls[key?.toLowerCase()] = [value ?? ''];
              this.formKeys.push({
                key,
                title,
                type,
                value: val ?? '',
                reportingType: input.reportingType,
                attributeClass: value,
              });
            }
          } else {
            if (!!this.data?.existingAttributesList?.value?.History || !!this.inputData?.existingAttributesList?.value?.History) {
              formControls[key?.toLowerCase()] = key.includes('as_of') ? [new Date()] : [val ?? ''];
            } else {
              formControls[key?.toLowerCase()] =
                key.includes('as_of') || key.toLowerCase().includes('date') ? [value ? value : new Date()] : [val ?? ''];
            }

            if (Array.isArray(this.handleDropDownTypes(input))) {
              if ((typeof val == 'string' && val?.trim() == '') || !val) {
                formControls[key?.toLowerCase()] = !!required ? ['', [Validators.required]] : [0];
                val = !!input?.defaultType ? input?.defaultType : '';
                value = !!input?.defaultType ? input?.defaultType : '';
              } else {
                formControls[key?.toLowerCase()] = !!required ? [`${val}`, [Validators.required]] : [`${val}`, []];
              }
            }
            this.formKeys.push({
              key,
              title,
              type,
              value: val ? `${val}` : '',
              required: !!required,
            });
          }
        }
      });
    }

    this.premiumForm = this.fb?.group(formControls);
    this.subscription.add(
      timer(3000)
        .pipe(take(1))
        .subscribe(() => {
          this.cd.detectChanges();
        })
    );
    if (!!this.inputData && !!this.premiumForm) {
      this.subscription.add(
        this.premiumForm.valueChanges.subscribe(() => {
          this.btnDisabledEmitter.emit(this.premiumForm.invalid);
        })
      );
    }
    this.cd.detectChanges();
  }
  toNumber(str) {
    const num = Number(str);
    return Number.isFinite(num) ? true : false;
  }
  ngAfterViewInit(): void {
    const dialogEl = this.formTemplate?.nativeElement?.querySelector('input');
    setTimeout(() => {
      dialogEl?.focus();
      dialogEl?.click();
      dialogEl.setSelectionRange(0, dialogEl.value.length); // Highlight the value
    }, 350);

    this.cd.detectChanges();
  }
  submitForm() {
    const formats = ['MM/DD/YYYY', 'MM-DD-YYYY', 'YYYY-MM-DD', 'YYYY/MM/DD', moment.ISO_8601];
    // if effective_date  or as_of bug arises, switch back to removing the || (or) condition of item.key.includes('as_of') and key.includes('as_of'))
    const hasDateControl = this.formKeys.find((item) => item.key.includes('date') || item.key.includes('as_of'));
    const dateKey = Object.keys(this.premiumForm.controls).find((key) => key.includes('date') || key.includes('as_of'));
    const date =
      hasDateControl && !Array.isArray(hasDateControl?.type)
        ? moment(this.premiumForm.controls[dateKey].value ?? moment(), formats).format('YYYY-MM-DD')
        : null;
    const hasBalanceControl = this.formKeys.find((item) => item.key.includes('balance') || item.type.includes('Dollar Input'));
    let updatedObject = { ...this.premiumForm.value };

    let payload: any = {
      type: !!this.inputData ? this.inputData?.title : this.data?.attributeData?.type ?? this.data?.triggeredFrom,
      user_id: !!this.inputData ? this.inputData?.user_id : this.data?.userId,
      vault_user_form_id: !!this.inputData ? this.inputData?.vault_user_form_id : this.data?.currentFormId,
    };

    if (this.premiumForm.controls['effective_date']) {
      updatedObject = (({ effective_date, ...rest }) => rest)(this.premiumForm.value);
      updatedObject = this.convertObjectValuesToNumbers(updatedObject);
      payload = {
        ...payload,
        value: JSON.stringify({
          ...updatedObject,
          effective_date: date,
        }),
        effective_date: date,
      };
    } else {
      dateKey && !Array.isArray(hasDateControl?.type) ? this.premiumForm.controls[dateKey].setValue(`${date}`) : '';
      let updatedObject = this.convertObjectValuesToNumbers({ ...this.premiumForm.value });

      payload = {
        ...payload,
        value: JSON.stringify(updatedObject),
      };
    }
    updatedObject = this.convertObjectValuesToNumbers({ ...this.premiumForm.value });

    if (!!this.data?.attributeData?.id || !!this.data?.balanceData?.id || !!this.inputData?.existingAttributesList?.getFormValues?.id) {
      payload = {
        ...payload,
        id: this.data?.attributeData?.id ?? this.data?.balanceData?.id ?? this.inputData?.existingAttributesList?.getFormValues?.id,
      };

      if (!!this.data?.existingAttributesList?.value?.History || !!this.inputData?.History) {
        const value = this.premiumForm.controls['as_of']
          ? this.premiumForm.controls['as_of'].value
          : this.premiumForm.controls['effective_date']
          ? this.premiumForm.controls['effective_date'].value
          : moment().format('YYYY-MM-DD');
        let date = moment(value, formats).format('YYYY-MM-DD') ?? moment().format('YYYY-MM-DD');
        if (value) {
          this.premiumForm.controls['as_of']
            ? this.premiumForm.removeControl('as_of')
            : this.premiumForm.controls['effective_date']
            ? this.premiumForm.removeControl('effective_date')
            : '';
        }
        const dynamicValue = this.premiumForm.controls[hasBalanceControl?.key].value;
        const additional_data = JSON.stringify(this.convertObjectValuesToNumbers((({ [dynamicValue]: _, ...rest }) => rest)(this.premiumForm.value)));
        payload = {
          ...payload,
          value: this.premiumForm.controls[hasBalanceControl.key].value,
          type_class: null,
          type_class_parent: null,
          register_type: this.data?.existingAttributesList?.value?.vault_form_code?.trim(),
          effective_date: `${date}`,
          additional_data,
        };

        if (!!this.incomeExpenseDropdown?.categoryValue) {
          payload = {
            ...payload,
            type_class: this.incomeExpenseDropdown?.categoryValue ?? null,
            type_class_parent: this.incomeExpenseDropdown?.parentCategory ?? null,
          };
        }
        delete payload.type;
        delete payload.value;
        payload = {
          ...payload,
          attribute_type: !!this.inputData ? this.inputData?.attribute_type : this.data.attribute_type,
        };

        this.data?.triggeredFrom !== 'register-balance' ? (delete payload?.id, this.addBalance(payload)) : this.updateBalance(payload);
      } else {
        payload = {
          ...payload,
          attribute_type: !!this.inputData ? this.inputData?.attribute_type : this.data?.attribute_type,
        };

        if (this.inputData?.existingAttributesList?.getFormValues.id) {
          delete payload.attribute_label;
          payload = {
            ...payload,
            type: this.inputData?.vault_form_code ?? this.data?.attributeData?.vault_form_code,
          };
        }
        this.updateFinancialAttribute({
          ...payload,
          attribute_type: !!this.inputData ? this.inputData?.attribute_type : this.data?.attribute_type,
        });
      }
    } else {
      if (this.data?.existingAttributesList?.value?.History || this.inputData?.history) {
        let date = this.premiumForm.controls['as_of']
          ? this.premiumForm.controls['as_of'].value
          : this.premiumForm.controls['effective_date']
          ? this.premiumForm.controls['effective_date'].value
          : moment().format('YYYY-MM-DD');
        date = moment(date, formats).format('YYYY-MM-DD') ?? moment().format('YYYY-MM-DD');
        this.premiumForm.controls['as_of']
          ? this.premiumForm.removeControl('as_of')
          : this.premiumForm.controls['effective_date']
          ? this.premiumForm.removeControl('effective_date')
          : '';
        const dynamicValue = this.premiumForm.controls[hasBalanceControl.key].value;
        const additional_data = JSON.stringify(this.convertObjectValuesToNumbers((({ [dynamicValue]: _, ...rest }) => rest)(this.premiumForm.value)));

        payload = {
          ...payload,
          value: this.premiumForm.controls[hasBalanceControl.key].value,
          type_class: null,
          type_class_parent: null,
          register_type: this.data?.existingAttributesList?.value?.vault_form_code?.trim() ?? this.inputData?.vault_form_code?.trim(),
          effective_date: moment(date, formats).format('YYYY-MM-DD') ?? moment().format('YYYY-MM-DD'),
          additional_data,
        };
        if (!!this.incomeExpenseDropdown?.categoryValue) {
          payload = {
            ...payload,
            type_class: this.incomeExpenseDropdown?.categoryValue ?? null,
            type_class_parent: this.incomeExpenseDropdown?.parentCategory ?? null,
          };
        }

        delete payload.type;
        delete payload.value;
        payload = {
          ...payload,
          attribute_type: !!this.inputData ? this.inputData?.attribute_type : this.data?.attribute_type,
        };

        this.addBalance({ ...payload, attribute_type: this.data.attribute_type });
      } else {
        payload = {
          ...payload,
          attribute_type: !!this.inputData ? this.inputData?.attribute_type : this.data?.attribute_type,
          type: !!this.inputData ? this.inputData?.vault_form_code : this.data.existingAttributesList?.value?.vault_form_code,
        };
        this.subscription.add(
          this.documentService.createVaultUserFormFinancialAttribute(payload).subscribe(
            (res) => {
              this.attributesService?.isSetupDoneSubmittingAttributes?.next(true);
              if (this.hide_mark_complete == true) {
                this.toastr.success(`Saved successfully`, '', {
                  positionClass: 'toast-bottom-left',
                });
              }
              const attribute_type = !!this.inputData ? this.inputData?.attribute_type : this.data?.attribute_type;
              const form_name = !!this.inputData ? this.inputData?.form_name : this.data?.form_name;
              const title = !!this.inputData ? this.inputData?.title : this.data?.title;
              const source = this.fromSetup ? 'setup' : 'view';
              this.trackEventsInMixPanel([
                {
                  eventName: EVENTS[`template_${source}_add_${attribute_type}_attributes`],
                  data: { attribute_type, form_name, title },
                },
              ]);
              if (res.status == 200) {
                this.dialogRef.close({
                  isSuccess: true,
                });
              } else {
                this.dialogRef.close('update');
              }
            },
            (error: any) => {
              this.toastr.error(error);
            }
          )
        );
      }
    }
  }

  updateFinancialAttribute(payload: unknown) {
    this.subscription.add(
      this.documentService.updateVaultUserFormFinancialAttribute(payload).subscribe(
        (res) => {
          this.attributesService.isSetupDoneSubmittingAttributes.next(true);
          if (this.hide_mark_complete == true) {
            this.toastr.success(`Saved successfully`, '', {
              positionClass: 'toast-bottom-left',
            });
          }
          const attribute_type = !!this.inputData ? this.inputData?.attribute_type : this.data?.attribute_type;
          const form_name = !!this.inputData ? this.inputData?.form_name : this.data?.form_name;
          const title = !!this.inputData ? this.inputData?.title : this.data?.title;
          const source = this.fromSetup ? 'setup' : 'view';
          this.trackEventsInMixPanel([
            {
              eventName: EVENTS[`template_${source}_edit_${attribute_type}_attributes`],
              data: { attribute_type, form_name, title },
            },
          ]);

          {
            if (res.status == 200) {
              const dialogCloseData = {
                isSuccess: true,
              };
              this.dialogRef.close(dialogCloseData);
            }
          }
        },
        (error: any) => {
          this.toastr.error(error);
        }
      )
    );
  }
  checkInput2(ev, control) {
    const elControlValue = this.premiumForm.controls[control?.key].value;
    if (!elControlValue || elControlValue.trim() == '') this.premiumForm.controls[control?.key].setValue(0);
    if (!elControlValue.includes('.')) {
      this.premiumForm.controls[control?.key].setValue(Number(elControlValue));
    } else {
      if (elControlValue.split('.')[1].trim() !== '') {
        // console.log('has value after full stop');
      }
    }
    this.handleInputCalculations();
  }
  checkInput(ev, control) {
    let passkeys: Array<any> = [95, 96, 97, 98, 99, 100, 101, 102, 103, 104, 105, 106, 109];
    const { which, key } = ev;
    if (!/[0-9\b.]/.test(key) && !passkeys.includes(which) && key !== 'Backspace') {
      if (key == 'Backspace') {
      } else {
        return ev.preventDefault();
      }
    }
    const controlValue = this.premiumForm.controls[control?.key].value;

    const hasDecimal = `${controlValue}`.includes('.');
    const decimal = `${controlValue}`.split('.');
    if (!!hasDecimal) {
      const has8thDecimal = decimal[1].length >= 8;
      if (has8thDecimal && key !== 'Backspace') return ev.preventDefault();
    }
  }

  setValue(ev: any | any, type?: any) {
    const key = type?.key?.toLowerCase();
    if (key == 'distribution_type' && ev !== 'Other') {
      const hasDistribution_type = this.formKeys.find((item) => item.key == 'enter_distribution_type');
      if (hasDistribution_type) {
        this.formKeys.splice(2, 1);
        this.premiumForm.removeControl('enter_distribution_type');
        this.cd.detectChanges();
      }
    }
    if (key == 'distribution_type' && ev == 'Other') {
      const enter_distribution_type = {
        key: 'enter_distribution_type',
        title: 'Enter Distribution Type',
        type: 'Text Input',
        value: '',
      };
      // add enter_distribution_type to the 3rd position of the formKeys array
      this.formKeys.splice(2, 0, enter_distribution_type);

      // Add the new form control dynamically to the form
      this.premiumForm.addControl('enter_distribution_type', this.fb.control(' '));
      this.cd.detectChanges();
    }

    if (typeof type !== 'string' && (type?.type !== 'search' || type?.key.toLowerCase().includes('unit_of_measurement'))) {
      this.handleInputCalculations();
    }
    const symbolValue = (ev as string)?.split('#');
    const symbol = symbolValue[0];
    // const value = symbolValue[1];
    if (typeof type == 'string') {
      this.premiumForm.controls[`${type}`].setValue(`${ev}`);
    } else {
      if (type?.type == 'search' || type.key.toLowerCase().includes('unit_of_measurement')) {
        if (type.key == 'cryptocurrency_type') {
          this.subscription.add(
            this.userService.getCryptoCurrencySymbolValue(symbol).subscribe((apiRes) => {
              // console.log({ apiRes });
              if (apiRes?.status != 'error') {
                this.formKeys.forEach((item) => {
                  if (item.hasError) delete item.hasError;
                  if (item.key == 'market_value_for_type') item.value = apiRes.regularMarketPrice.raw;
                  this.cd.detectChanges();
                });
                this.premiumForm.controls['market_value_for_type'].setValue(apiRes.regularMarketPrice.raw);
                this.handleInputCalculations();
              } else {
                this.formKeys.forEach((item) => {
                  if (item.key == 'market_value_for_type') item.hasError = apiRes?.message;
                });
              }
              this.cd.detectChanges();
            })
          );
        }

        if (type.key == 'currency_type') {
          this.subscription.add(
            this.userService.getForeignCurrencySymbolValue(symbol).subscribe((apiRes) => {
              // console.log({ apiRes });
              if (!!apiRes?.success || apiRes?.success == true) {
                this.formKeys.forEach((item) => {
                  if (item.hasError) delete item.hasError;
                  if (item.key == 'market_value_for_type') item.value = apiRes.info.rate;
                  // console.log({ item });

                  this.cd.detectChanges();
                });
                this.premiumForm.controls['market_value_for_type'].setValue(apiRes.info.rate);
                // console.log({ formValue: this.premiumForm.value });

                this.handleInputCalculations();
              } else {
                this.formKeys.forEach((item) => {
                  if (item.key == 'market_value_for_type') item.hasError = apiRes?.error.info;
                });
              }
              this.cd.detectChanges();
              this.cd.detectChanges();
            })
          );
        }

        if (type.key == 'ticker_symbol') {
          this.subscription.add(
            this.userService.getTickerSymbolValue(symbol).subscribe((apiRes) => {
              if (apiRes?.status != 'error') {
                this.formKeys.forEach((item) => {
                  if (item.hasError) delete item.hasError;
                  if (item.key == 'value_per_share') item.value = apiRes.regularMarketPrice.raw;
                  this.cd.detectChanges();
                });
                this.premiumForm.controls['value_per_share'].setValue(apiRes.regularMarketPrice.raw);
                this.handleInputCalculations();
              } else {
                this.formKeys.forEach((item) => {
                  if (item.key == 'value_per_share') item.hasError = apiRes?.message;
                });
              }
              this.cd.detectChanges();
            })
          );
        }
        if (type.key == 'metal_type' || type.key.toLowerCase().includes('unit_of_measurement')) {
          const umv = this.premiumForm.controls['unit_of_measurement'].value;
          // console.log({ symbol, umv });

          if (type.key == 'metal_type') {
            this.subscription.add(
              this.userService.getMetalSymbolValue(symbol, umv !== 0 && !!umv ? umv : 'ounce').subscribe((apiRes) => {
                // console.log({ apiRes });
                this.formKeys.forEach((item) => {
                  if (!!Object.values(apiRes.rates)[0]) {
                    if (item.hasError) delete item.hasError;
                    if (item.key == 'market_value_for_type') item.value = Object.values(apiRes.rates)[0] ?? 0;
                  } else {
                    if (item.key == 'market_value_for_type') item.hasError = apiRes?.validationMessage[0];
                  }
                  this.cd.detectChanges();
                });
                if (!!Object.values(apiRes.rates)[0]) {
                  this.premiumForm.controls['market_value_for_type'].setValue(Object.values(apiRes.rates)[0] ?? 0);
                  this.handleInputCalculations();
                }
                this.cd.detectChanges();
              })
            );
          } else {
            const symbolValue = this.premiumForm.controls['metal_type'].value.split('#');
            const symbol = symbolValue[0];
            // console.log({ symbol, umv });
            this.subscription.add(
              this.userService.getMetalSymbolValue(symbol, umv !== 0 && !!umv ? umv : 'ounce').subscribe((apiRes) => {
                // console.log({ apiRes });
                this.formKeys.forEach((item) => {
                  if (!!Object.values(apiRes.rates)[0]) {
                    if (item.hasError) delete item.hasError;
                    if (item.key == 'market_value_for_type') item.value = Object.values(apiRes.rates)[0] ?? 0;
                  } else {
                    if (item.key == 'market_value_for_type') item.hasError = apiRes?.validationMessage[0];
                  }
                  this.cd.detectChanges();
                });
                if (!!Object.values(apiRes.rates)[0]) {
                  this.premiumForm.controls['market_value_for_type'].setValue(Object.values(apiRes.rates)[0] ?? 0);
                  this.handleInputCalculations();
                }
                this.cd.detectChanges();
              })
            );
          }
        }
      }
    }
    this.handleInputCalculations();
  }
  hasError = null;
  handleInputCalculations() {
    if (!!this.inputData) {
      this.inputData?.existingAttributesList?.value?.inputs?.forEach((input, index) => {
        const { key } = input;
        if (!!input?.multiply) {
          let result = [];
          input?.multiply?.forEach((item) => {
            if (this.premiumForm.controls.hasOwnProperty(item)) {
              let parsedVal: string | any =
                typeof this.premiumForm.controls[item].value == 'number'
                  ? this.premiumForm.controls[item].value
                  : this.premiumForm.controls[item].value?.trim() == ''
                  ? 0
                  : parseFloat(this.premiumForm.controls[item].value?.replace(/,/g, ''));

              if (typeof parsedVal == 'number') {
              } else if (typeof parsedVal == 'string' && parsedVal.trim() == '') {
                parsedVal = 0;
              }
              if (Number.isNaN(parsedVal)) {
                parsedVal = 0;
              }
              if (item.toLowerCase().includes('percentage')) {
                parsedVal = parsedVal / 100;
              }
              result.push(parsedVal);
            }
          });

          result.length > 0
            ? this.premiumForm.controls[key].setValue(result.reduce((accumulator, currentValue) => accumulator * currentValue, 1).toFixed(8))
            : this.premiumForm.controls[key].setValue(0);

          this.formKeys.forEach((item) => {
            if (item.key == key) {
              item.value = result.reduce((accumulator, currentValue) => accumulator * currentValue, 1).toFixed(8);
            }
          });
        }
        if (!!input?.subtract) {
          let result = [];
          input?.subtract?.forEach((item) => {
            if (this.premiumForm.controls.hasOwnProperty(item)) {
              result.push(
                typeof this.premiumForm.controls[item].value == 'number'
                  ? this.premiumForm.controls[item].value
                  : this.premiumForm.controls[item].value?.trim() == ''
                  ? 0
                  : parseFloat(this.premiumForm.controls[item].value?.replace(/,/g, ''))
              );
            }
          });
          result.length > 0
            ? this.premiumForm.controls[key].setValue(result.reduce((accumulator, currentValue) => accumulator - currentValue).toFixed(8))
            : this.premiumForm.controls[key].setValue(0);
          this.formKeys.forEach((item) => {
            if (item.key == key) {
              item.value = result.reduce((accumulator, currentValue) => accumulator - currentValue).toFixed(8);
            }
          });
        }
        if (!!input?.addition) {
          let result = [];
          input?.addition?.forEach((item) => {
            if (this.premiumForm.controls.hasOwnProperty(item)) {
              const { value } = this.premiumForm.controls[item];
              if (
                (typeof value !== 'number' && typeof value !== 'string' && (value?._i || typeof value == 'object')) ||
                item?.toLowerCase() == 'commitment_start_date' ||
                item.toLowerCase().includes('date')
              ) {
                const momentDate = moment(value, 'MM-DD-YYYY').toDate();
                const date = new Date(value);
                `${date}` !== 'Invalid Date' ? result.push(date) : result.push(momentDate);
              } else {
                result.push(typeof value == 'number' ? value : value?.trim() == '' ? 0 : parseFloat(value?.replace(/,/g, '')));
              }
            }
          });

          if (result[0] instanceof Date) {
            this.premiumForm.controls[key].setValue(this.getDateAfterAddingMonths(result[0], result[1]));
            this.formKeys.forEach((item) => {
              if (item.key == key) {
                item.value = this.getDateAfterAddingMonths(result[0], result[1]);
              }
            });
          } else {
            result.length > 0
              ? this.premiumForm.controls[key].setValue(
                  result.reduce((accumulator, currentValue) => accumulator + currentValue).toFixed(key !== 'target_loan_completion' ? 8 : 0)
                )
              : this.premiumForm.controls[key].setValue(0);
            this.formKeys.forEach((item) => {
              if (item.key == key) {
                item.value = result
                  .reduce((accumulator, currentValue) => accumulator + currentValue)
                  .toFixed(key !== 'target_loan_completion' ? 8 : 0);
              }
            });
          }
        }
        this.cd.detectChanges();
      });
    } else {
      this.data?.existingAttributesList?.value?.inputs?.forEach((input, index) => {
        const { key } = input;
        if (!!input?.multiply) {
          let result = [];
          input?.multiply?.forEach((item) => {
            if (this.premiumForm.controls.hasOwnProperty(item)) {
              let parsedVal: any =
                typeof this.premiumForm.controls[item].value == 'number'
                  ? this.premiumForm.controls[item].value
                  : this.premiumForm.controls[item].value?.trim() == ''
                  ? 0
                  : parseFloat(this.premiumForm.controls[item].value?.replace(/,/g, ''));

              if (typeof parsedVal == 'number') {
              } else if (typeof parsedVal == 'string' && parsedVal.trim() == '') {
                parsedVal = 0;
              }
              if (Number.isNaN(parsedVal)) {
                parsedVal = 0;
              }
              if (item.toLowerCase().includes('percentage')) {
                parsedVal = parsedVal / 100;
              }
              result.push(parsedVal);
            }
          });

          result.length > 0
            ? this.premiumForm.controls[key].setValue(result.reduce((accumulator, currentValue) => accumulator * currentValue, 1).toFixed(8))
            : this.premiumForm.controls[key].setValue(0);

          this.formKeys.forEach((item) => {
            if (item.key == key) {
              item.value = this.addCommaToNumber(result.reduce((accumulator, currentValue) => accumulator * currentValue, 1).toFixed(8));
            }
          });
        }
        if (!!input?.subtract) {
          let result = [];
          input?.subtract?.forEach((item) => {
            if (this.premiumForm.controls.hasOwnProperty(item)) {
              result.push(
                typeof this.premiumForm.controls[item].value == 'number'
                  ? this.premiumForm.controls[item].value
                  : this.premiumForm.controls[item].value?.trim() == ''
                  ? 0
                  : parseFloat(this.premiumForm.controls[item].value?.replace(/,/g, ''))
              );
            }
          });
          result.length > 0
            ? this.premiumForm.controls[key].setValue(result.reduce((accumulator, currentValue) => accumulator - currentValue).toFixed(8))
            : this.premiumForm.controls[key].setValue(0);
          this.formKeys.forEach((item) => {
            if (item.key == key) {
              item.value = this.addCommaToNumber(result.reduce((accumulator, currentValue) => accumulator - currentValue).toFixed(8));
            }
          });
        }
        if (!!input?.addition) {
          let result = [];
          input?.addition?.forEach((item) => {
            if (this.premiumForm.controls.hasOwnProperty(item)) {
              const { value } = this.premiumForm.controls[item];
              // console.log({ item, value });

              if (
                (typeof value !== 'number' && typeof value !== 'string' && (value?._i || typeof value == 'object')) ||
                item.toLowerCase().includes('date')
              ) {
                const momentDate = moment(value, 'MM-DD-YYYY').toDate();
                const date = new Date(value);
                `${date}` !== 'Invalid Date' ? result.push(date) : result.push(momentDate);
              } else {
                result.push(typeof value == 'number' ? value : value?.trim() == '' ? 0 : parseFloat(value?.replace(/,/g, '')));
              }
            }
          });
          if (result[0] instanceof Date) {
            this.premiumForm.controls[key].setValue(this.getDateAfterAddingMonths(result[0], result[1]));
            this.formKeys.forEach((item) => {
              if (item.key == key) {
                item.value = this.getDateAfterAddingMonths(result[0], result[1]);
              }
            });
          } else {
            result.length > 0
              ? this.premiumForm.controls[key].setValue(
                  result.reduce((accumulator, currentValue) => accumulator + currentValue).toFixed(key !== 'target_loan_completion' ? 8 : 0)
                )
              : this.premiumForm.controls[key].setValue(0);
            this.formKeys.forEach((item) => {
              if (item.key == key) {
                item.value = this.addCommaToNumber(
                  result.reduce((accumulator, currentValue) => accumulator + currentValue).toFixed(key !== 'target_loan_completion' ? 8 : 0)
                );
              }
            });
          }
        }
        this.cd.detectChanges();
      });
    }
  }

  trackEventsInMixPanel(events: { eventName: EventNames; data?: any }[]) {
    this.userService.trackEventsInMixPanel(events).then((response) => {});
  }

  addCommaToNumber(number) {
    return Number(number).toLocaleString(undefined, { minimumFractionDigits: 8 });
  }

  handleValueForSearch(key, title) {
    return `${key}#${title}`;
  }
  convertStringToDate(date: any) {
    if (date instanceof Date) {
      return this.moment(date).format('MMM DD, YYYY');
    } else if (typeof date == 'string') {
      const [day, month, year] = date?.includes('/') ? date?.split('/') : date?.includes('-') ? date?.split('-') : date?.split('/');
      const dateObject = date?.includes('/') ? new Date(`${year}-${month}-${day}`) : new Date(date);
      return this.moment(dateObject).format('MMM DD, YYYY');
    }

    return date;
    // ?? this.moment(new Date());
  }
  convertObjectValuesToNumbers(obj: any) {
    const result = {};
    for (const [key, value] of Object.entries(obj)) {
      if (typeof value === 'string' && !isNaN((value as any).replace(/,/g, ''))) {
        const usdValueKey = this.formKeys.find((item) => item?.key === key);
        if (
          !!usdValueKey &&
          Number.isNaN(parseFloat(value.replace(/,/g, ''))) &&
          !Array.isArray(usdValueKey?.type) &&
          (usdValueKey?.type?.toLowerCase().includes('dollar') || usdValueKey?.type?.toLowerCase().includes('numeric'))
        ) {
          result[key] = '0';
        } else {
          result[key] = parseFloat(value.replace(/,/g, ''));
        }
      } else {
        const isCryptoKey = this.formKeys?.find((item) => item?.key === key);
        if (!isCryptoKey) result[key] = value;
        if (isCryptoKey && isCryptoKey.type == 'search' && value == 0) {
          result[key] = '';
        } else {
          result[key] = value;
        }
      }
    }
    return result;
  }

  transFormToNumber(val) {
    if (typeof val == 'number') return val;
    return parseFloat(val.replace(/,/g, ''));
  }
  createArrayFromNumber(number: number) {
    const result: any[] = [];
    for (let i = 1; i <= number; i++) {
      result.push(`${i}`);
    }
    return result;
  }
  metalTypes = ['Gold', 'Silver', 'Platinum', 'Palladium'];
  handleDropDownTypes(control: any) {
    try {
      return this.insuranceDropDownList[control.type] ? this.insuranceDropDownList[control.type] : null;
    } catch (error) {
      return null;
    }
  }
  getYearsArray(startYear) {
    const currentYear = new Date().getFullYear();
    const yearsArray = [];
    for (let year = startYear; year <= currentYear; year++) {
      yearsArray.push(`${year}`);
    }

    return yearsArray;
  }
  getType(controlValue) {
    if (Array.isArray(controlValue)) return true;
    return false;
  }
  hasCategoryDropDown(control: any): boolean {
    if (
      control?.key == 'Income & Expense' ||
      control?.type == 'Income & Expense' ||
      (control?.key == 'reporting' && control?.type != 'Assets & Liabilities')
    )
      return true;
  }
  addBalance(BALANCE_DETAIL) {
    this.subscription.add(
      this.shareService.createBalanceEntry(BALANCE_DETAIL).subscribe(
        (response) => {
          this.attributesService.isSetupDoneSubmittingAttributes.next(true);
          this.toastr.success(this.hide_mark_complete ? 'Saved successfully' : 'Value Added successfully.');
          const attribute_type = !!this.inputData ? this.inputData?.attribute_type : this.data?.attribute_type;
          const form_name = !!this.inputData ? this.inputData?.form_name : this.data?.form_name;
          const title = !!this.inputData ? this.inputData?.title : this.data?.title;
          const source = this.fromSetup ? 'setup' : 'view';
          this.trackEventsInMixPanel([
            {
              eventName: EVENTS[`template_${source}_add_${attribute_type}_attributes`],
              data: { attribute_type, form_name, title },
            },
          ]);

          this.dialogRef.close({
            isSuccess: true,
            // setupItem: this.setupItem
          });
        },
        (error) => {
          this.toastr.error(error);
        }
      )
    );
  }
  updateBalance(BALANCE_DETAIL) {
    this.subscription.add(
      this.shareService.updateBalanceEntry(BALANCE_DETAIL).subscribe(
        (response) => {
          this.attributesService.isSetupDoneSubmittingAttributes.next(true);
          this.toastr.success('Value Added successfully.');
          const attribute_type = !!this.inputData ? this.inputData?.attribute_type : this.data?.attribute_type;
          const form_name = !!this.inputData ? this.inputData?.form_name : this.data?.form_name;
          const title = !!this.inputData ? this.inputData?.title : this.data?.title;
          const source = this.fromSetup ? 'setup' : 'view';
          this.trackEventsInMixPanel([
            {
              eventName: EVENTS[`template_${source}_edit_${attribute_type}_attributes`],
              data: { attribute_type, form_name, title },
            },
          ]);

          this.dialogRef.close({
            isSuccess: true,
          });
        },
        (error) => {
          this.toastr.error(error);
        }
      )
    );
  }

  getListOfCryptoCoins() {
    this.cryptoItems = ['Btc', 'Eth', 'Sol', 'Usdt', 'Usdc'];
  }

  getDateAfterAddingMonths(date, months) {
    const newDate = moment(date).add(months, 'months');
    const formattedDate = newDate.format('YYYY-MM-DD');
    return formattedDate;
  }

  handleDateChange(value_, controlName, control_) {
    let value: string | null = value_;
    let control: typeof control_;

    if (value) {
      value = value.replace(/\//g, '-');
      control = {
        ...control_,
        value,
      };

      this.premiumForm.controls[controlName].setValue(value);
      this.setValue(value, control);
      this.formKeys = this.formKeys.map((key) => {
        if (key.key === controlName) {
          return {
            ...key,
            value: value,
          };
        }
        return key;
      });
      this.cd.detectChanges();
    }
  }
}
